import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaceInput from "@/components/PlaceInput";
import { User, Sparkles, Save, Crown, Check, MessageCircle, Globe } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ActivationPaywall from "@/components/ActivationPaywall";

const LANG_OPTIONS = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिन्दी (Hindi)" },
  { value: "ta", label: "தமிழ் (Tamil)" },
  { value: "mr", label: "मराठी (Marathi)" },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    birth_dob: "",
    birth_time: "",
    birth_place: "",
    chat_language: "en",
  });
  const [city, setCity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        birth_dob: user.birth_dob || "",
        birth_time: user.birth_time || "",
        birth_place: user.birth_place || "",
        chat_language: user.chat_language || "en",
      });
      if (user.birth_lat && user.birth_lon) {
        setCity({
          name: user.birth_place || "Saved location",
          country: "",
          lat: user.birth_lat,
          lon: user.birth_lon,
          tz: user.birth_tz,
        });
      }
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        birth_dob: form.birth_dob || null,
        birth_time: form.birth_time || null,
        birth_place: city ? `${city.name}${city.country ? `, ${city.country}` : ""}` : null,
        birth_lat: city ? city.lat : null,
        birth_lon: city ? city.lon : null,
        birth_tz: city ? city.tz : null,
        chat_language: form.chat_language,
      };
      const { data } = await api.patch("/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl text-slate-50 mb-2">Your Profile</h1>
          <p className="text-slate-400">Birth details power your charts & Astro Talk context.</p>
        </div>
        {user?.is_activated ? (
          <span data-testid="activated-badge" className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs uppercase tracking-wider flex items-center gap-1">
            <Check className="w-3 h-3" /> Activated
          </span>
        ) : (
          <Button onClick={() => setPaywallOpen(true)} data-testid="profile-activate-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full gap-2">
            <Crown className="w-4 h-4" /> Activate Premium
          </Button>
        )}
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSave} className="space-y-5" data-testid="profile-form">
          <div className="flex items-center gap-2 text-amber-300 text-sm uppercase tracking-wider">
            <User className="w-4 h-4" /> Account
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="profile-name-input"
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Email (cannot change)</Label>
              <Input value={user?.email || ""} disabled className="mt-1.5 bg-slate-900/30 border-white/5 text-slate-500" />
            </div>
          </div>

          <div className="flex items-center gap-2 text-amber-300 text-sm uppercase tracking-wider pt-4">
            <Sparkles className="w-4 h-4" /> Birth Details (used by Astro Talk & charts)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300">Date of Birth</Label>
              <Input type="date" value={form.birth_dob} onChange={(e) => setForm({ ...form, birth_dob: e.target.value })}
                     data-testid="profile-dob-input"
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Time of Birth</Label>
              <Input type="time" value={form.birth_time} onChange={(e) => setForm({ ...form, birth_time: e.target.value })}
                     data-testid="profile-time-input"
                     className="mt-1.5 bg-slate-900/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Birth Place</Label>
              <div className="mt-1.5">
                <PlaceInput value={city} onChange={setCity} testId="profile-city" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-amber-300 text-sm uppercase tracking-wider pt-4">
            <MessageCircle className="w-4 h-4" /> Chat Preferences
          </div>
          <div>
            <Label className="text-slate-300 flex items-center gap-1"><Globe className="w-3 h-3" /> Astro Talk reply language</Label>
            <Select value={form.chat_language} onValueChange={(v) => setForm({ ...form, chat_language: v })}>
              <SelectTrigger data-testid="profile-chat-lang" className="mt-1.5 bg-slate-900/50 border-white/10 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-200">
                {LANG_OPTIONS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} data-testid="profile-save-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium gap-2 px-6">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Link to="/dashboard">
              <Button type="button" variant="outline" className="rounded-full border-white/10 text-slate-300 hover:text-amber-300">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </Card>

      <ActivationPaywall open={paywallOpen} onOpenChange={setPaywallOpen} feature="all premium tools" />
    </div>
  );
}
